import type {
  AuthenticatedPrincipal,
  BalanceTransactionType,
  SessionEndReason,
} from '@autodialer/shared';

import { PREVIEW_MODE } from '../preview/preview-mode';

/**
 * Schmaler Zugriff auf die REST-Schnittstelle.
 *
 * Das Sitzungstoken liegt in einem httpOnly-Cookie; JavaScript sieht es nicht
 * und muss es auch nicht verwalten. Deshalb genügt `credentials: 'include'`.
 */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(code);
  }

  /** Die Sitzung ist serverseitig beendet - der Nutzer muss sich neu anmelden. */
  get sessionEnded(): boolean {
    return this.status === 401;
  }

  get sessionEndReason(): SessionEndReason | undefined {
    const reason = this.details?.reason;
    return typeof reason === 'string' ? (reason as SessionEndReason) : undefined;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (PREVIEW_MODE) {
    // Entwicklerwerkzeug: die Vorschau antwortet mit Beispieldaten, damit die
    // Oberflaeche ohne Backend beurteilt werden kann. Ausserhalb der Vorschau
    // entfaellt dieser Zweig beim Bauen vollstaendig.
    const { previewRequest } = await import('../preview/preview-api');
    return previewRequest<T>(method, path, body);
  }

  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError('offline', 0);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = (await response.json().catch(() => null)) as
    | (Record<string, unknown> & { code?: string; details?: Record<string, unknown> })
    | null;

  if (!response.ok) {
    throw new ApiError(payload?.code ?? 'unknown', response.status, payload?.details);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body ?? {}),
};

export interface PrincipalResponse {
  principal: AuthenticatedPrincipal;
}

export interface CallerView {
  id: string;
  username: string | null;
  email: string | null;
  isAdmin: boolean;
  isOwner: boolean;
  isActive: boolean;
  locale: 'de' | 'en' | 'tr';
  createdAt: string;
}

export interface AccountView {
  id: string;
  customerNumber: string;
  accountName: string | null;
  ownerEmail: string;
  status: string;
  locale: 'de' | 'en' | 'tr';
  balance: string;
  pricePerMinute: string;
  registeredAt: string;
}

export const authApi = {
  me: () => api.get<PrincipalResponse>('/auth/me'),
  login: (identifier: string, password: string) =>
    api.post<PrincipalResponse>('/auth/login', { identifier, password }),
  /**
   * Anmeldung der Systemadministration.
   *
   * Eigener Endpunkt und eigene Adresse - ein Hauptadmin ist kein Benutzer
   * eines Mandanten (01_roles_and_accounts.md).
   */
  platformLogin: (email: string, password: string) =>
    api.post<PrincipalResponse>('/auth/platform/login', { email, password }),
  logout: () => api.post<{ ok: true }>('/auth/logout'),
  requestPasswordReset: (email: string) => api.post<{ ok: true }>('/auth/password-reset/request', { email }),
  confirmPasswordReset: (token: string, password: string) =>
    api.post<{ ok: true }>('/auth/password-reset/confirm', { token, password }),
};

export const accountApi = {
  register: (email: string, password: string, passwordConfirmation: string) =>
    api.post<{ customerNumber: string }>('/registration', { email, password, passwordConfirmation }),
  get: () => api.get<{ account: AccountView }>('/account'),
  rename: (accountName: string | null) => api.patch<{ account: AccountView }>('/account/name', { accountName }),
  remove: (password: string) => api.delete<{ ok: true }>('/account', { password, confirmed: true }),
};

/**
 * Eine Buchung, wie der Kunde sie sieht (09_billing_payments.md).
 *
 * Ohne interne Notiz, ohne Vorgangsnummer des Zahlungsanbieters und ohne
 * Angabe, wer gebucht hat.
 */
export interface BalanceTransactionView {
  id: string;
  type: BalanceTransactionType;
  /** Vorzeichenbehaftet: positiv = Gutschrift, negativ = Abbuchung. */
  amount: string;
  balanceAfter: string;
  note: string | null;
  createdAt: string;
}

export interface BalanceOverview {
  balance: string;
  minTopUpAmount: string;
  transactions: BalanceTransactionView[];
}

/**
 * Guthaben.
 *
 * Es gibt hier ausschliesslich Guthaben - keine Minutenpakete und keine
 * Produktpakete. Leistungen werden intern aus dem Guthaben bezahlt.
 */
export const balanceApi = {
  overview: () => api.get<BalanceOverview>('/balance'),
  transactions: (offset = 0) =>
    api.get<{ transactions: BalanceTransactionView[] }>('/balance/transactions?offset=' + offset),
  /** Startet eine Aufladung. Der Mindestbetrag wird serverseitig geprueft. */
  topUp: (amount: string) => api.post<{ ok?: true }>('/balance/topup', { amount }),
};

export const usersApi = {
  listCallers: () => api.get<{ callers: CallerView[] }>('/callers'),
  createCaller: (username: string, password: string) =>
    api.post<{ caller: CallerView }>('/callers', { username, password }),
  setPassword: (id: string, password: string) => api.post<{ ok: true }>(`/callers/${id}/password`, { password }),
  setAdminRights: (id: string, isAdmin: boolean) =>
    api.patch<{ caller: CallerView }>(`/callers/${id}/admin-rights`, { isAdmin }),
  setActive: (id: string, isActive: boolean) =>
    api.patch<{ caller: CallerView }>(`/callers/${id}/active`, { isActive }),
  changeOwnPassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: true }>('/me/password', { currentPassword, newPassword }),
  setLocale: (locale: 'de' | 'en' | 'tr') => api.patch<{ locale: string }>('/me/locale', { locale }),
};
